import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styles from './IntermediateTable.module.css';
import NumericField from '../../form/NumericField/NumericField';
import SelectField from '../../form/SelectField/SelectField';
import CadenciaModal from './CadenciaModal';
import { useIntermediateTableLogic } from './IntermediateTableLogic';
import { parseCurrency } from '../../../utils/formatters';

const IntermediateTable = ({ onIntermediatesChange, initialIntermediates = [], maxLimit, replicateValue = true, disableValuesExceptFirst = false, initialMonthOffset = 3, isDisabled = false, isFlex = false, renda = 0, valorImovel = 0, planoSelecionado = '', eSAD = false, onAlertaValorMaximo = null, prosolutoA = null, onAlertaDataMaxima = null, quantidadeParcelasPSA = 0 }) => {
  const [showCadenciaModal, setShowCadenciaModal] = useState(false);

  // Calcular valor máximo permitido para intermediária
  const calcularValorMaximoIntermediaria = (index = -1, totalIntermediarias = 0) => {
    // Prazo de Obra: 4x a renda
    if (planoSelecionado === 'Prazo de Obra') {
      return renda * 4;
    }
    
    // Pós Obra: última é 15% do valor imóvel, restantes 4x a renda
    if (planoSelecionado === 'Pós Obra') {
      
      if(!eSAD){
        return renda * 4;
      }

      const isUltima = index >= 0 && totalIntermediarias > 0 && index === totalIntermediarias - 1;
      if (isUltima) {
        return valorImovel * 0.15;
      }
      return renda * 4;
    }
    
    // Financiamento Bancário (FB): 4x renda, última é 10% valor imóvel
    if (planoSelecionado === 'Financiamento Bancário') {
      // Verificar se é a última intermediária
      const isUltima = index >= 0 && totalIntermediarias > 0 && index === totalIntermediarias - 1;
      if (isUltima) {
        return valorImovel * 0.10;
      }
      return renda * 4;
    }
    
    // Padrão: sem limite adicional
    return Infinity;
  };

  const {
    intermediates,
    addIntermediate,
    removeIntermediate,
    updateIntermediateValue,
    updateIntermediateField,
    isLimitReached
  } = useIntermediateTableLogic(onIntermediatesChange, maxLimit, initialIntermediates, replicateValue, initialMonthOffset, isFlex);

  // Data atual
  const agora = new Date();
  const mesAtual = agora.getMonth() + 1;
  const anoAtual = agora.getFullYear();

  // Base de anos (será filtrada depois de calcularDataMaximaPermitida)
  const todosAnosOptions = Array.from({ length: 11 }, (_, i) => ({
    value: anoAtual + i,
    label: `${anoAtual + i}`
  }));

  // Calcular a diferença em meses entre duas datas
  const calcularDiferencaMeses = (mes1, ano1, mes2, ano2) => {
    return (ano2 - ano1) * 12 + (mes2 - mes1);
  };

  // Validar se a data selecionada respeita o mínimo de 3 meses de diferença das demais intermediárias
  const isDataComDiferencaMinimaValida = (mesNovo, anoNovo, indexAtual) => {
    const minimoMeses = 3;
    
    for (let i = 0; i < intermediates.length; i++) {
      if (i === indexAtual) continue; // Pular o registro atual
      
      const diferenca = Math.abs(calcularDiferencaMeses(
        intermediates[i].mesIntermediaria,
        intermediates[i].anoIntermediaria,
        mesNovo,
        anoNovo
      ));
      
      if (diferenca < minimoMeses && diferenca !== 0) {
        return false;
      }
    }
    
    return true;
  };



  // Validar se já existe outra intermediária na mesma data
  const isDataDuplicada = (mesNovo, anoNovo, indexAtual) => {
    return intermediates.some((item, i) => {
      if (i === indexAtual) return false;
      return item.mesIntermediaria === mesNovo && item.anoIntermediaria === anoNovo;
    });
  };

  // Calcular data máxima permitida baseada no PSA (para Flex)
  const calcularDataMaximaPermitida = () => {
    if (!isFlex) return null;
    
    let diasMaximos = 0;

    if (!quantidadeParcelasPSA) return null;
    diasMaximos = quantidadeParcelasPSA - 1;

    let mesMaximo = mesAtual + diasMaximos;
    let anoMaximo = anoAtual;
    
    // Ajustar ano se mês ultrapassar 12
    while (mesMaximo > 12) {
      mesMaximo -= 12;
      anoMaximo += 1;
    }
    
    return { mes: mesMaximo, ano: anoMaximo };
  };

  const calcularDataMinimaSAD = () => {
    if (!eSAD) return null;

    let mesMinimo = mesAtual + 3;
    let anoMinimo = anoAtual;
    while (mesMinimo > 12) {
      mesMinimo -= 12;
      anoMinimo++;
    }

    return { mes: mesMinimo, ano: anoMinimo };
  };

  const dataMinimaSAD = calcularDataMinimaSAD();

  const dataMaximaPSA = calcularDataMaximaPermitida();
  const anosOptions = todosAnosOptions
  .filter((a) => !dataMaximaPSA?.ano || a.value <= dataMaximaPSA.ano)
  .filter((a) => !dataMinimaSAD?.ano || a.value >= dataMinimaSAD.ano);

  const isDataAntesMinimaSAD = (mes, ano) => {
    if (!dataMinimaSAD) return false;
    if (ano < dataMinimaSAD.ano) return true;
    if (ano === dataMinimaSAD.ano && mes < dataMinimaSAD.mes) return true;
    return false;
  };

  const isDataAposProsoluto = (mes, ano) => {
    if (!isFlex) return false;
    
    const dataMaxima = dataMaximaPSA;
    if (!dataMaxima) return false;
    
    if (ano > dataMaxima.ano) return true;
    if (ano === dataMaxima.ano && mes > dataMaxima.mes) return true;
    return false;
  };

  const getMesesValidos = (ano) => {
    const todasOpcoes = [
      { value: 1, label: 'Janeiro' },
      { value: 2, label: 'Fevereiro' },
      { value: 3, label: 'Março' },
      { value: 4, label: 'Abril' },
      { value: 5, label: 'Maio' },
      { value: 6, label: 'Junho' },
      { value: 7, label: 'Julho' },
      { value: 8, label: 'Agosto' },
      { value: 9, label: 'Setembro' },
      { value: 10, label: 'Outubro' },
      { value: 11, label: 'Novembro' },
      { value: 12, label: 'Dezembro' }
    ];

    const dataMaxima = dataMaximaPSA;
    let opcoes = todasOpcoes;

    if (ano === anoAtual) {
      opcoes = opcoes.filter(m => m.value > mesAtual);
    }

    if (dataMinimaSAD) {
      if (ano < dataMinimaSAD.ano) {
        opcoes = [];
      } else if (ano === dataMinimaSAD.ano) {
        opcoes = opcoes.filter(m => m.value >= dataMinimaSAD.mes);
      }
    }

    // Filtrar meses que ultrapassam o prosolutoA (para Flex)
    if (isFlex && dataMaxima && ano === dataMaxima.ano) {
      opcoes = opcoes.filter(m => m.value <= dataMaxima.mes);
    } else if (isFlex && dataMaxima && ano > dataMaxima.ano) {
      opcoes = [];
    }

    return opcoes;
  };

  const handleOpenCadenciaModal = () => {
    if (isFlex && intermediates.length > 0) {
      setShowCadenciaModal(true);
    } else {
      addIntermediate();
    }
  };

  // Formatar valor como moeda sem duplicar R$
  const formatarMoeda = (valor) => {
    return valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const handleCadenciaConfirm = (cadencia) => {
    addIntermediate(cadencia);
    setShowCadenciaModal(false);
  };

  const lastIntermediate = intermediates.length > 0 
    ? intermediates[intermediates.length - 1] 
    : null;

  return (
    <div className={styles.tableContainer}>
      <div className={styles.tableResponsive}>
        <table className={styles.table} id="tabelaIntermediarias">
          <thead>
            <tr>
              <th>Descrição</th>
              <th>Valor (R$)</th>
              <th>Mês</th>
              <th>Ano</th>
              <th className={styles.actionsHeader}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {intermediates.length > 0 ? (
              intermediates.map((item, index) => (
                <tr key={item.id}>
                  <td className={styles.descriptionCell}>{item.description}</td>
                  <td className={styles.valueCell}>
                    <NumericField
                      id={`valor_intermediaria_${item.id}`}
                      value={item.value}
                      onChange={(e) => {
                        // Usar parseCurrency do projeto para evitar erros de cálculo
                        const novoValor = parseCurrency(e.target.value) || 0;
                        const maxPermitido = calcularValorMaximoIntermediaria(index, intermediates.length);
                        
                        // Verificar se ultrapassa o máximo
                        if (novoValor > maxPermitido && isFlex && onAlertaValorMaximo) {
                          onAlertaValorMaximo(`Valor de R$ ${formatarMoeda(novoValor)} ultrapassa o máximo permitido de R$ ${formatarMoeda(maxPermitido)}`);
                        }
                        
                        updateIntermediateValue(item.id, e.target.value);
                      }}
                      currency={true}
                      disabled={index > 0 && disableValuesExceptFirst}
                      max={calcularValorMaximoIntermediaria(index, intermediates.length)}
                      title={isFlex ? `Máximo permitido: R$ ${formatarMoeda(calcularValorMaximoIntermediaria(index, intermediates.length))}` : ''}
                    />
                  </td>
                  <td className={styles.selectCell}>
                    <SelectField
                      id={`mes_intermediaria_${item.id}`}
                      value={item.mesIntermediaria}
                      onChange={(e) => {
                        const novoMes = Number.parseInt(e.target.value, 10);

                        // Validar data duplicada
                        if (isDataDuplicada(novoMes, item.anoIntermediaria, index)) {
                          if (onAlertaDataMaxima) onAlertaDataMaxima('Já existe uma intermediária cadastrada nessa data.');
                          return;
                        }

                        if (isDataAntesMinimaSAD(novoMes, item.anoIntermediaria)) {
                          if (onAlertaDataMaxima) onAlertaDataMaxima(`A data não pode ser anterior a ${dataMinimaSAD.mes.toString().padStart(2, '0')}/${dataMinimaSAD.ano}.`);
                          return;
                        }

                        // Validar diferença mínima de 3 meses
                        if (isFlex && !isDataComDiferencaMinimaValida(novoMes, item.anoIntermediaria, index)) {
                          onAlertaDataMaxima(`A data deve ter no mínimo 3 meses de diferença das demais intermediárias.`);
                          return;
                        }
                        
                        if (isDataAposProsoluto(novoMes, item.anoIntermediaria) && isFlex && onAlertaDataMaxima) {
                          const dataMaxima = dataMaximaPSA;
                          onAlertaDataMaxima(`A data não pode ser posterior a ${dataMaxima.mes.toString().padStart(2, '0')}/${dataMaxima.ano}.`);
                        } else {
                          updateIntermediateField(
                            item.id,
                            'mesIntermediaria',
                            novoMes
                          );
                        }
                      }}
                      options={getMesesValidos(item.anoIntermediaria)}
                      label=""
                      title={isFlex && dataMaximaPSA ? `Data máxima: ${dataMaximaPSA.mes}/${dataMaximaPSA.ano}` : ''}
                    />
                  </td>
                  <td className={styles.selectCell}>
                    <SelectField
                      id={`ano_intermediaria_${item.id}`}
                      value={item.anoIntermediaria}
                      onChange={(e) => {
                        const novoAno = Number.parseInt(e.target.value, 10);

                        // Validar data duplicada
                        if (isDataDuplicada(item.mesIntermediaria, novoAno, index)) {
                          onAlertaDataMaxima?.('Já existe uma intermediária cadastrada nessa data.');
                          return;
                        }

                        if (isDataAntesMinimaSAD(item.mesIntermediaria, novoAno)) {
                          onAlertaDataMaxima?.(`A data não pode ser anterior a ${dataMinimaSAD.mes.toString().padStart(2, '0')}/${dataMinimaSAD.ano}. O mês foi ajustado para o limite.`);
                          updateIntermediateField(item.id, 'anoIntermediaria', novoAno);
                          updateIntermediateField(item.id, 'mesIntermediaria', dataMinimaSAD.mes);
                          return;
                        }

                        // Validar diferença mínima de 3 meses
                        if (isFlex && !isDataComDiferencaMinimaValida(item.mesIntermediaria, novoAno, index)) {
                          onAlertaDataMaxima(`A data deve ter no mínimo 3 meses de diferença das demais intermediárias.`);
                          return;
                        }

                        if (isDataAposProsoluto(item.mesIntermediaria, novoAno) && isFlex) {
                          const dataMaxima = dataMaximaPSA;

                          onAlertaDataMaxima?.(`A data não pode ser posterior a ${dataMaxima.mes.toString().padStart(2, '0')}/${dataMaxima.ano}. O mês foi ajustado para o limite.`);
                          updateIntermediateField(item.id, 'anoIntermediaria', novoAno);
                          updateIntermediateField(item.id, 'mesIntermediaria', dataMaxima.mes);
                        } else {
                          updateIntermediateField(item.id, 'anoIntermediaria', novoAno);

                          if (novoAno === anoAtual && item.mesIntermediaria <= mesAtual) {
                            // Math.min para evitar overflow caso mesAtual seja Dezembro (12)
                            updateIntermediateField(item.id, 'mesIntermediaria', Math.min(mesAtual + 1, 12));
                          }
                        }
                      }}
                      options={anosOptions}
                      label=""
                      title={isFlex && dataMaximaPSA ? `Data máxima: ${dataMaximaPSA.mes}/${dataMaximaPSA.ano}` : ''}
                    />
                  </td>
                  <td className={styles.actionsCell}>
                    <div className={styles.actionButtons}>

                      {index === intermediates.length - 1 && !isLimitReached && !isDisabled && (
                        <button
                          id="adicionarIntermediaria"
                          className={styles.addBtn}
                          onClick={handleOpenCadenciaModal}
                          title="Adicionar Parcela Intermediária"
                          aria-label="Adicionar Parcela Intermediária"
                          type="button"
                        >
                          <i className="fas fa-plus"></i>
                        </button>
                      )}
                      <button
                        className={styles.deleteBtn}
                        title="Remover"
                        onClick={() => removeIntermediate(item.id)}
                        type="button"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className={styles.emptyMessage}>
                  Nenhuma parcela intermediária adicionada
                </td>
                <td className={styles.actionsCell}>
                  <div className={styles.actionButtons}>
                    <button
                      id="adicionarIntermediaria"
                      className={styles.addBtn}
                      onClick={handleOpenCadenciaModal}
                      title="Adicionar Parcela Intermediária"
                      aria-label="Adicionar Parcela Intermediária"
                      type="button"
                      disabled={isLimitReached || isDisabled}
                    >
                      <i className="fas fa-plus"></i>
                    </button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {isFlex && lastIntermediate && (
        <CadenciaModal
          isOpen={showCadenciaModal}
          lastMonth={lastIntermediate.mesIntermediaria}
          lastYear={lastIntermediate.anoIntermediaria}
          onConfirm={handleCadenciaConfirm}
          onCancel={() => setShowCadenciaModal(false)}
        />
      )}
    </div>
  );
};

IntermediateTable.propTypes = {
  onIntermediatesChange: PropTypes.func.isRequired,
  initialIntermediates: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    description: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    mesIntermediaria: PropTypes.number,
    anoIntermediaria: PropTypes.number,
    cadencia: PropTypes.number
  })),
  maxLimit: PropTypes.number,
  replicateValue: PropTypes.bool,
  disableValuesExceptFirst: PropTypes.bool,
  initialMonthOffset: PropTypes.number,
  isDisabled: PropTypes.bool,
  isFlex: PropTypes.bool,
  renda: PropTypes.number,
  valorImovel: PropTypes.number,
  planoSelecionado: PropTypes.string,
  eSAD: PropTypes.bool,
  onAlertaValorMaximo: PropTypes.func,
  prosolutoA: PropTypes.number,
  onAlertaDataMaxima: PropTypes.func,
  quantidadeParcelasPSA: PropTypes.number
};

IntermediateTable.defaultProps = {
  initialIntermediates: [],
  maxLimit: undefined,
  replicateValue: true,
  disableValuesExceptFirst: false,
  initialMonthOffset: 3,
  isDisabled: false,
  isFlex: false,
  renda: 0,
  valorImovel: 0,
  planoSelecionado: '',
  eSAD: false,
  onAlertaValorMaximo: null,
  prosolutoA: null,
  onAlertaDataMaxima: null,
  quantidadeParcelasPSA: 0
};

export default IntermediateTable;
